import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuotes } from "../hooks/QuotesContext.js";
import { changeDirection, formatPercent } from "../lib/format.js";
import type { StockBasket } from "../store/workspace.js";
import {
  CloseIcon,
  MoreIcon,
  PlusIcon,
  UploadIcon,
} from "./Icons.js";

interface BasketTabsProps {
  baskets: StockBasket[];
  activeBasketId: string;
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onRename: (id: string, name: string) => void;
  onDuplicate: (id: string) => void;
  onExport: (basket: StockBasket) => void;
  onShare: (basket: StockBasket) => string;
  onImportFile: (file: File) => Promise<void>;
  onRemove: (id: string) => void;
}

export function BasketTabs({
  baskets,
  activeBasketId,
  onSelect,
  onCreate,
  onRename,
  onDuplicate,
  onExport,
  onShare,
  onImportFile,
  onRemove,
}: BasketTabsProps) {
  const quotes = useQuotes();
  const [isCreating, setIsCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [actionBasketId, setActionBasketId] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<{
    basketName: string;
    url: string;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const renameRef = useRef<HTMLInputElement | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);
  const actionBasket = baskets.find(
    (basket) => basket.id === actionBasketId,
  );

  useEffect(() => {
    if (isCreating) inputRef.current?.focus();
  }, [isCreating]);

  useEffect(() => {
    if (isRenaming) renameRef.current?.focus();
  }, [isRenaming]);

  useEffect(() => {
    if (!notice) return undefined;
    const timeout = window.setTimeout(() => setNotice(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    const closeShareDialog = () => setShareLink(null);
    window.addEventListener("hashchange", closeShareDialog);
    return () => window.removeEventListener("hashchange", closeShareDialog);
  }, []);

  const submitNewBasket = (event: FormEvent) => {
    event.preventDefault();
    const name = draftName.trim();
    if (!name) return;
    onCreate(name);
    setDraftName("");
    setIsCreating(false);
  };

  const closeActions = () => {
    setActionBasketId(null);
    setIsRenaming(false);
    setRenameDraft("");
  };

  const runAction = (action: () => void, message?: string) => {
    try {
      action();
      closeActions();
      if (message) setNotice(message);
    } catch (error) {
      setNotice((error as Error).message);
    }
  };

  return (
    <>
      <nav className="basket-bar" aria-label="Stock baskets">
        <div className="basket-tabs">
          {baskets.map((basket) => {
            const symbols = [
              ...new Set(basket.cards.map((card) => card.symbol.toUpperCase())),
            ];
            const percentages = symbols
              .map((symbol) => quotes[symbol]?.changePercent)
              .filter(
                (value): value is number =>
                  typeof value === "number" && Number.isFinite(value),
              );
            const averageChange =
              percentages.length > 0
                ? percentages.reduce((sum, value) => sum + value, 0) /
                  percentages.length
                : null;
            const direction = changeDirection(averageChange);
            const isActive = basket.id === activeBasketId;

            return (
              <div
                key={basket.id}
                className={`basket-tab ${isActive ? "basket-tab--active" : ""}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className="basket-tab__select"
                  onClick={() => onSelect(basket.id)}
                >
                  <span className="basket-tab__name">{basket.name}</span>
                  <span className="basket-tab__meta">
                    {symbols.length} {symbols.length === 1 ? "stock" : "stocks"}
                    {averageChange != null && (
                      <>
                        <span aria-hidden> · </span>
                        <span
                          className={`basket-tab__change basket-tab__change--${direction}`}
                          title="Equal-weight average daily change"
                        >
                          {formatPercent(averageChange)} today
                        </span>
                      </>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  className="basket-tab__more"
                  aria-label={`Manage ${basket.name} basket`}
                  title="Basket actions"
                  onClick={() => setActionBasketId(basket.id)}
                >
                  <MoreIcon />
                </button>
              </div>
            );
          })}

          {isCreating ? (
            <form className="basket-create" onSubmit={submitNewBasket}>
              <input
                ref={inputRef}
                value={draftName}
                maxLength={40}
                placeholder={`Basket ${baskets.length + 1}`}
                aria-label="New basket name"
                onChange={(event) => setDraftName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setDraftName("");
                    setIsCreating(false);
                  }
                }}
              />
              <button type="submit" disabled={!draftName.trim()}>
                Create
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="basket-add"
              onClick={() => setIsCreating(true)}
              aria-label="Create stock basket"
              title="Create stock basket"
            >
              <PlusIcon />
              <span>New basket</span>
            </button>
          )}

          <button
            type="button"
            className="basket-import-button"
            onClick={() => importRef.current?.click()}
          >
            <UploadIcon />
            <span>Import</span>
          </button>
          <input
            ref={importRef}
            type="file"
            accept=".json,application/json"
            className="visually-hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              void onImportFile(file).catch((error: Error) =>
                setNotice(error.message),
              );
            }}
          />
        </div>
        {notice && (
          <div className="basket-notice" role="status">
            {notice}
          </div>
        )}
      </nav>

      {actionBasket && (
        <div
          className="basket-actions-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeActions();
          }}
        >
          <section
            className="basket-actions"
            role="dialog"
            aria-modal="true"
            aria-labelledby="basket-actions-title"
          >
            <header className="basket-actions__header">
              <div>
                <strong id="basket-actions-title">{actionBasket.name}</strong>
                <span>
                  {actionBasket.cards.length}{" "}
                  {actionBasket.cards.length === 1 ? "chart" : "charts"}
                </span>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={closeActions}
                aria-label="Close basket actions"
              >
                <CloseIcon />
              </button>
            </header>

            {isRenaming ? (
              <form
                className="basket-rename"
                onSubmit={(event) => {
                  event.preventDefault();
                  const name = renameDraft.trim();
                  if (!name) return;
                  onRename(actionBasket.id, name);
                  closeActions();
                  setNotice("Basket renamed");
                }}
              >
                <label htmlFor="basket-rename-input">Basket name</label>
                <input
                  id="basket-rename-input"
                  ref={renameRef}
                  value={renameDraft}
                  maxLength={40}
                  onChange={(event) => setRenameDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setIsRenaming(false);
                  }}
                />
                <div>
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={() => setIsRenaming(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="button button--primary"
                    disabled={!renameDraft.trim()}
                  >
                    Save
                  </button>
                </div>
              </form>
            ) : (
              <div className="basket-actions__list">
                <button
                  type="button"
                  onClick={() => {
                    setRenameDraft(actionBasket.name);
                    setIsRenaming(true);
                  }}
                >
                  <strong>Rename</strong>
                  <span>Change this basket’s tab name</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    runAction(
                      () => onDuplicate(actionBasket.id),
                      "Basket duplicated",
                    )
                  }
                >
                  <strong>Duplicate</strong>
                  <span>Copy every chart, setting, and position</span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    runAction(
                      () => onExport(actionBasket),
                      "Basket exported",
                    )
                  }
                >
                  <strong>Export file</strong>
                  <span>Download a portable ChartAndGo basket</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      setShareLink({
                        basketName: actionBasket.name,
                        url: onShare(actionBasket),
                      });
                      closeActions();
                    } catch (error) {
                      setNotice((error as Error).message);
                    }
                  }}
                >
                  <strong>Create share link</strong>
                  <span>Anyone with the link can preview and import it</span>
                </button>
                {baskets.length > 1 && (
                  <button
                    type="button"
                    className="basket-actions__danger"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete “${actionBasket.name}” and all of its charts?`,
                        )
                      ) {
                        onRemove(actionBasket.id);
                        closeActions();
                      }
                    }}
                  >
                    <strong>Delete</strong>
                    <span>Permanently remove this basket</span>
                  </button>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {shareLink && (
        <div
          className="basket-import-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShareLink(null);
          }}
        >
          <section
            className="basket-share-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="basket-share-title"
          >
            <header className="basket-actions__header">
              <div>
                <strong id="basket-share-title">Share {shareLink.basketName}</strong>
                <span>The basket data is contained in this link.</span>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setShareLink(null)}
                aria-label="Close share link"
              >
                <CloseIcon />
              </button>
            </header>
            <div className="basket-share-dialog__body">
              <label htmlFor="basket-share-url">Share link</label>
              <input
                id="basket-share-url"
                readOnly
                value={shareLink.url}
                onFocus={(event) => event.currentTarget.select()}
              />
              <p>
                Opening this link shows a preview before adding the basket.
              </p>
            </div>
            <footer className="basket-import-dialog__footer">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setShareLink(null)}
              >
                Done
              </button>
              <button
                type="button"
                className="button button--primary"
                onClick={() => {
                  void copyText(shareLink.url)
                    .then(() => setNotice("Share link copied"))
                    .catch((error: Error) => setNotice(error.message));
                }}
              >
                Copy link
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall back to a temporary selection when clipboard permission is denied.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Select the link and copy it manually.");
}
